import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Search, LifeBuoy, Clock, Play, RotateCcw, CheckCircle2, Building2, MessageSquare, X, ChevronDown, Save } from 'lucide-react';

import { API_BASE as API } from '../config';

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: 'bg-amber-100 text-amber-700',  icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700',    icon: Play },
  returned:    { label: 'Returned',    color: 'bg-orange-100 text-orange-700', icon: RotateCcw },
  resolved:    { label: 'Resolved',    color: 'bg-green-100 text-green-700',   icon: CheckCircle2 },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`} data-testid={`assistance-badge-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function NoteModal({ title, onSubmit, onClose, submitLabel, submitColor }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    setSubmitting(true);
    await onSubmit(note);
    setSubmitting(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="note-modal-backdrop">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="note-modal">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            rows={3}
            placeholder="Add a note (optional)..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="note-modal-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose} data-testid="note-modal-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className={submitColor} data-testid="note-modal-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClaimDetailPanel({ claim, onClose, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [noteModal, setNoteModal] = useState(null);

  useEffect(() => {
    setForm({
      status: claim.status,
      description: claim.description || '',
      fault: claim.fault || '',
      location: claim.location || '',
      drivers_name: claim.drivers_name || '',
      truck_no: claim.truck_no || '',
      trailer_no: claim.trailer_no || '',
    });
  }, [claim]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/support/claims`, { report_no: claim.report_no, ...form }, { withCredentials: true });
      toast.success('Claim updated');
      onUpdate();
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusAction = async (newStatus, note) => {
    try {
      const body = { report_no: claim.report_no, assistance_status: newStatus };
      if (note) body.operator_note = note;
      await axios.put(`${API}/support/claims`, body, { withCredentials: true });
      toast.success(`Status changed to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setNoteModal(null);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose} data-testid="claim-detail-panel">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="font-bold text-gray-900 text-base">{claim.report_no}</h3>
            <p className="text-xs text-gray-500">{claim.tenant_name} {claim.org_name ? `/ ${claim.org_name}` : ''} / {claim.company_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={claim.assistance_status} />
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="claim-detail-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Assistance Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><LifeBuoy className="w-3.5 h-3.5" /> Assistance Request</p>
            {claim.assistance_note && <p className="text-sm text-amber-800">{claim.assistance_note}</p>}
            {claim.operator_note && (
              <div className="mt-2 bg-white border border-amber-100 rounded p-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Operator Note</p>
                <p className="text-sm text-gray-700">{claim.operator_note}</p>
              </div>
            )}
            <p className="text-[10px] text-amber-600">Requested {new Date(claim.assistance_requested_at).toLocaleString()}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {claim.assistance_status === 'pending' && (
              <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white" onClick={() => handleStatusAction('in_progress')} data-testid="action-start-working">
                <Play className="w-3.5 h-3.5 mr-1" /> Start Working
              </Button>
            )}
            {claim.assistance_status === 'in_progress' && (
              <>
                <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => handleStatusAction('resolved')} data-testid="action-resolve">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Resolved
                </Button>
                <Button size="sm" variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => setNoteModal('returned')} data-testid="action-return">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Stop Working
                </Button>
              </>
            )}
            {claim.assistance_status === 'returned' && (
              <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white" onClick={() => handleStatusAction('in_progress')} data-testid="action-resume">
                <Play className="w-3.5 h-3.5 mr-1" /> Resume Working
              </Button>
            )}
            {!editing && claim.assistance_status !== 'resolved' && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="action-edit-claim">
                Edit Claim
              </Button>
            )}
          </div>

          {/* Claim Info (read-only or edit) */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500 text-xs">Driver</span><p className="font-medium">{editing ? <Input value={form.drivers_name} onChange={(e) => setForm({...form, drivers_name: e.target.value})} className="h-8 text-sm" data-testid="edit-drivers-name" /> : claim.drivers_name}</p></div>
            <div><span className="text-gray-500 text-xs">Status</span><p className="font-medium">{editing ? (
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full h-8 text-sm border border-gray-300 rounded px-2" data-testid="edit-status">
                <option value="New">New</option><option value="In Process">In Process</option><option value="Closed">Closed</option>
              </select>
            ) : claim.status}</p></div>
            <div><span className="text-gray-500 text-xs">Truck</span><p className="font-medium">{editing ? <Input value={form.truck_no} onChange={(e) => setForm({...form, truck_no: e.target.value})} className="h-8 text-sm" data-testid="edit-truck" /> : claim.truck_no}</p></div>
            <div><span className="text-gray-500 text-xs">Trailer</span><p className="font-medium">{editing ? <Input value={form.trailer_no} onChange={(e) => setForm({...form, trailer_no: e.target.value})} className="h-8 text-sm" data-testid="edit-trailer" /> : claim.trailer_no}</p></div>
            <div><span className="text-gray-500 text-xs">Fault</span><p className="font-medium">{editing ? <Input value={form.fault} onChange={(e) => setForm({...form, fault: e.target.value})} className="h-8 text-sm" data-testid="edit-fault" /> : claim.fault}</p></div>
            <div><span className="text-gray-500 text-xs">Location</span><p className="font-medium">{editing ? <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="h-8 text-sm" data-testid="edit-location" /> : claim.location}</p></div>
            <div className="col-span-2"><span className="text-gray-500 text-xs">Description</span>{editing ? (
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full border border-gray-300 rounded p-2 text-sm mt-1" rows={2} data-testid="edit-description" />
            ) : <p className="text-gray-700">{claim.description}</p>}</div>
            <div><span className="text-gray-500 text-xs">Date of Loss</span><p className="font-medium">{claim.date_of_loss ? new Date(claim.date_of_loss).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'}</p></div>
            <div><span className="text-gray-500 text-xs">Loss Types</span><p className="font-medium">{['al','pd','cd','ocac','wc'].filter(t => claim[t]).map(t => t.toUpperCase()).join(', ') || '-'}</p></div>
          </div>

          {editing && (
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="edit-cancel">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="edit-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Changes
              </Button>
            </div>
          )}
        </div>
      </div>

      {noteModal && (
        <NoteModal
          title="Stop Working — Add Note"
          submitLabel="Return Claim"
          submitColor="bg-orange-500 hover:bg-orange-600 text-white"
          onClose={() => setNoteModal(null)}
          onSubmit={(note) => handleStatusAction('returned', note)}
        />
      )}
    </div>
  );
}

export default function SupportQueue() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [search, setSearch] = useState('');

  const fetchQueue = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/support/queue${params}`, { withCredentials: true });
      setClaims(res.data);
    } catch {
      toast.error('Failed to load support queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const filtered = claims.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c.report_no?.toLowerCase().includes(s) ||
      c.company_name?.toLowerCase().includes(s) ||
      c.tenant_name?.toLowerCase().includes(s) ||
      c.drivers_name?.toLowerCase().includes(s) ||
      c.description?.toLowerCase().includes(s)
    );
  });

  const counts = {
    all: claims.length,
    pending: claims.filter((c) => c.assistance_status === 'pending').length,
    in_progress: claims.filter((c) => c.assistance_status === 'in_progress').length,
    returned: claims.filter((c) => c.assistance_status === 'returned').length,
    resolved: claims.filter((c) => c.assistance_status === 'resolved').length,
  };

  if (!user?.is_platform_operator) return null;

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8" data-testid="support-queue-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="support-queue-title">
              <LifeBuoy className="w-6 h-6 text-red-500" /> Support Queue
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">{counts.all} total requests across all tenants</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search claims..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10"
              data-testid="support-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[
              { key: '', label: 'All', count: counts.all },
              { key: 'pending', label: 'Pending', count: counts.pending },
              { key: 'in_progress', label: 'In Progress', count: counts.in_progress },
              { key: 'returned', label: 'Returned', count: counts.returned },
              { key: 'resolved', label: 'Resolved', count: counts.resolved },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => { setStatusFilter(f.key); setLoading(true); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === f.key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`filter-${f.key || 'all'}`}
              >
                {f.label} {f.count > 0 && <span className="ml-1 opacity-70">({f.count})</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-gray-400" data-testid="support-empty">
                <LifeBuoy className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No assistance requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Claim</th>
                      <th className="px-4 py-3">Tenant</th>
                      <th className="px-4 py-3 hidden md:table-cell">Company</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Driver</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 hidden md:table-cell">Requested</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((c) => (
                      <tr
                        key={c.report_no}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedClaim(c)}
                        data-testid={`queue-row-${c.report_no.replace(/\//g, '-')}`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 text-xs">{c.report_no}</p>
                          <p className="text-[10px] text-gray-400">{c.status}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-700">{c.tenant_name}</span>
                          </div>
                          {c.org_name && <p className="text-[10px] text-gray-400 ml-5">{c.org_name}</p>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.company_name}</td>
                        <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{c.drivers_name}</td>
                        <td className="px-4 py-3"><StatusBadge status={c.assistance_status} /></td>
                        <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                          {c.assistance_requested_at ? new Date(c.assistance_requested_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {c.assistance_note && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 max-w-[200px] truncate">
                              <MessageSquare className="w-3 h-3 flex-shrink-0" /> {c.assistance_note}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selectedClaim && (
        <ClaimDetailPanel
          claim={selectedClaim}
          onClose={() => setSelectedClaim(null)}
          onUpdate={() => { fetchQueue(); setSelectedClaim(null); }}
        />
      )}
    </div>
  );
}
